import { formatAmountToLocale, isNullOrUndefined, isRCCSubWithAvailableSize } from '@/shared/utils';
import { SubscriptionModel } from '../../../common/src';
import store from '@/store';

export function getSubscriptionCreationPrice(subscription: SubscriptionModel): number | undefined {
  if (!isNullOrUndefined(subscription.creationPrice)) {
    return subscription.creationPrice!;
  }
  return subscription.creationNormalPrice;
}

export function getSubscriptionCreationDiscountedPrice(subscription: SubscriptionModel): number | undefined {
  if (!isNullOrUndefined(subscription.creationPrice) && subscription.creationNormalPrice !== subscription.creationPrice) {
    return subscription.creationNormalPrice;
  }
  return undefined;
}

export function getSubscriptionPeriodPrice(subscription: SubscriptionModel): number | undefined {
  if (!isNullOrUndefined(subscription.campaignPrice)) {
    return subscription.campaignPrice!;
  }
  if (!isNullOrUndefined(subscription.periodPrice)) {
    return subscription.periodPrice!;
  }
  return subscription.periodNormalPrice;
}

export function getSubscriptionSubPeriodPrice(subscription: SubscriptionModel): number | undefined {
  const productGroupsEnablingSubPeriodPrice = store.state.config!.joinFlow.productsGroupThatIncludePeriodCalculation;

  if (
    !productGroupsEnablingSubPeriodPrice ||
    !subscription.periodNormalPrice ||
    !subscription.groups ||
    !subscription.periodLength ||
    subscription.periodLength === 1
  ) {
    return undefined;
  }

  const hasProductGroupEnablingSubPeriodPrice = subscription.groups.some(group => {
    return productGroupsEnablingSubPeriodPrice.includes(group.id + '');
  });

  if (!hasProductGroupEnablingSubPeriodPrice) {
    return undefined;
  }

  return Math.round(subscription.periodNormalPrice / subscription.periodLength);
}

export function getSubscriptionPeriodDiscountedPrice(subscription: SubscriptionModel): number | undefined {
  if (!isNullOrUndefined(subscription.campaignPrice) && subscription.periodNormalPrice !== subscription.campaignPrice) {
    return subscription.periodNormalPrice;
  }
  if (!isNullOrUndefined(subscription.periodPrice) && subscription.periodNormalPrice !== subscription.periodPrice) {
    return subscription.periodNormalPrice;
  }
  return undefined;
}

export function getPeriodDescription(subscription: SubscriptionModel):
  | {
      period: string;
      periodCount: number | undefined;
    }
  | undefined {
  if (subscription.periodUnit == undefined || !['EFT', 'RECURRING_CLIPCARD'].includes(subscription.subscriptionType)) {
    return undefined;
  }
  let periodCount: number | undefined;
  if (!isNullOrUndefined(subscription.periodLength) && subscription.periodLength! > 1) {
    periodCount = subscription.periodLength;
  }
  const period = subscription.periodUnit;

  return {
    period,
    periodCount,
  };
}

export function getCampainText(subscription: SubscriptionModel): string | undefined {
  return subscription.campaignText || subscription.periodPriceText || subscription.creationPriceText;
}

export function getPlanPeriodDescriptionTop(
  subscription: SubscriptionModel,
  $tc: (a: string, b: number) => string,
  discounted: boolean,
  priceOnBottomBanner = false
): string | undefined {
  const period = getPeriodDescription(subscription);
  if (period) {
    const showPerClipText = isRCCSubWithAvailableSize(subscription) && !priceOnBottomBanner;
    if (discounted) {
      return `${showPerClipText ? $tc('general.DISCOUNTED-PER-CLIP', period.periodCount || 1) + ' ' : ''}${$tc(
        'general.DISCOUNTED-PER-' + period.period,
        period.periodCount || 1
      )}`;
    } else {
      return `${showPerClipText ? $tc('general.PER-CLIP', period.periodCount || 1) + ' ' : ''}${$tc(
        'general.PER-' + period.period,
        period.periodCount || 1
      )}`;
    }
  }
  return undefined;
}

export function getPlanPeriodDescriptionBottom(subscription: SubscriptionModel, $t: (a: string) => string): string | undefined {
  const creationPrice = getSubscriptionCreationPrice(subscription);
  return '+ ' + formatAmountToLocale(creationPrice!, subscription.currency) + ' ' + $t('subscription.joiningFee');
}

export function getPlanDiscountedPriceDescription(subscription: SubscriptionModel): number | undefined {
  if (getSubscriptionPeriodDiscountedPrice(subscription) !== undefined) {
    return subscription.periodNormalPrice;
  }
  return undefined;
}

export function getPlanDiscountedPeriodDescriptionTop(
  subscription: SubscriptionModel,
  $tc: (a: string, b: number) => string
): string | undefined {
  return getPlanDiscountedPriceDescription(subscription) ? getPlanPeriodDescriptionTop(subscription, $tc, false) : undefined;
}

export function getPlanDiscountedPeriodDescriptionBottom(subscription: SubscriptionModel, $t: (a: string) => string): string | undefined {
  if (
    !isNullOrUndefined(subscription.creationNormalPrice) &&
    (getSubscriptionCreationDiscountedPrice(subscription) !== undefined || getPlanDiscountedPriceDescription(subscription) !== undefined)
  ) {
    return '+ ' + formatAmountToLocale(subscription.creationNormalPrice!, subscription.currency) + ' ' + $t('subscription.joiningFee');
  }
  return undefined;
}
